import mitt, {Handler} from 'mitt'
import {ref} from 'vue'

const emitter = mitt()

type EventHandler = {
    event: string,
    handler: Handler
}

const services = ref<Map<string, EmitterService>>(new Map<string, EmitterService>())

export type EmitterService = {
    onEvent: (event: string, handler: Handler) => void
    cleanUp: () => void
    onEvents: (events: string[], handler: Handler) => void
    incrementRefCount: () => void
}

const useSingletonEmitter = (service: string): EmitterService => {

    const existingService = services.value.get(service)
    if (existingService) {
        existingService.incrementRefCount()
        return existingService
    }

    const handlers: Map<string, Handler> = new Map()
    const refCount = ref(1)


    const incrementRefCount = () => {
        refCount.value++
    }

    const onEvent = (event: string, handler: Handler) => {
        if (!handlers.has(event)) {
            handlers.set(event, handler)
            emitter.on(event, handler)
        }
    }

    const onEvents = (events: string[], handler: Handler) => {
        events.forEach(e => {
            onEvent(e, handler)
        })
    }

    const cleanUp = () => {
        refCount.value--
        if (refCount.value === 0) {
            handlers.forEach((handler, event) => {
                emitter.off(event, handler)
            })
            services.value.delete(service)
        }
    }

    services.value.set(service, {onEvent, cleanUp, onEvents, incrementRefCount})

    return services.value.get(service) as EmitterService
}

const useEmitter = () => {

    const handlers: EventHandler[] = []

    const onEvent = (event: string, handler: Handler) => {
        handlers.push({event, handler})
        emitter.on(event, handler)
    }

    const onEvents = (events: string[], handler: Handler) => {
        events.forEach(e => {
            handlers.push({event: e, handler})
            emitter.on(e, handler)
        })
    }

    const cleanUp = () => {
        handlers.forEach(eventHandler => {
            emitter.off(eventHandler.event, eventHandler.handler)
        })
    }

    return {onEvent, cleanUp, onEvents}
}

export default emitter
export {useEmitter, useSingletonEmitter}